import React, { useCallback } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import UserGroups from '@components/ProfileMenu/UserGroups';
import { useAuthService } from '@core/auth.service';
import { toUser } from '@data/dto/user.dto';
import {
  faArrowRotateRight,
  faChevronDown,
  faPowerOff,
  faShieldHalved,
  faTriangleExclamation,
  faUsersGear,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useUser from '@hooks/useUser';
import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  IconButton,
  Link,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { getRouteByName, userCanSeeRoute } from '@plugins/router/default';
import { toast } from '@stores/actions/toast.action';
import { setUser } from '@stores/actions/user.action';
import Constants from 'constants/constants';
import OverrideTheme from 'themes/custom-theme-helper';

const useStyles = makeStyles((theme) => ({
  profileAvatar: {
    backgroundColor: 'white !important',
    marginRight: theme.spacing(1.25),
    width: 30,
    height: 30,
  },
  profileBtn: {
    borderRadius: 100,
    padding: `${OverrideTheme.getSpacing(1.3)}px`,
    backgroundColor: 'white',
    border: 'none !important',
    color: OverrideTheme.colors.textPrimary,
    fontSize: `${OverrideTheme.getSpacing(0.24)}rem`,
  },
  profileMenu: {
    boxShadow: OverrideTheme.shadows.primary,
    marginTop: 5,
    '& a': {
      color: OverrideTheme.colors.textPrimary,
    },
  },
  expandIcon: {
    color: OverrideTheme.colors.primary,
    marginLeft: theme.spacing(1.2),
    marginRight: theme.spacing(0.8),
  },
  profileDetails: {
    paddingBottom: 8,
    paddingHorizontal: 8,
    paddingTop: 5,
    cursor: 'initial',
  },
  noPaddingMenu: { paddingBottom: 0 },
  groupsList: { maxWidth: 260 },
  divider: { marginTop: 30, marginBottom: 20 },
  userIcon: {
    color: OverrideTheme.colors.primary,
  },
  decoIcon: {
    color: OverrideTheme.colors.primary,
    marginLeft: 5,
  },
}));

const ProfileMenu = (): JSX.Element => {
  const classes = useStyles();
  const { user, isUserAssistantCG, mainUserRole, isEmulated } = useUser();
  const dispatch = useDispatch();
  const { logout } = useAuthService();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const forceClose = () => {
    setOpen(false);
  };

  const handleClose = (event: React.MouseEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    forceClose();
  };

  const canUserAccess = (routeName: string) => {
    return userCanSeeRoute(user, getRouteByName(routeName));
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleLogout = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();

    const hasLoggedOut = await logout();

    if (hasLoggedOut) {
      dispatch(
        toast({
          message: t('modal.logout.success'),
          level: 'success',
        }),
      );

      dispatch(setUser(toUser({})));
      window.location.reload();
      return;
    }

    dispatch(
      toast({
        message: t('modal.logout.error'),
        level: 'error',
      }),
    );
  }, []);

  const avatar = `https://eu.ui-avatars.com/api/?name=${user.fullname}&background=136CAC&color=FFF`;

  return (
    <>
      <IconButton
        aria-label="account of current user"
        aria-controls="menu-list-grow"
        aria-haspopup="true"
        className={classes.profileBtn}
        onClick={handleToggle}
        centerRipple={false}
        ref={anchorRef}
      >
        <Avatar
          className={classes.profileAvatar}
          src={avatar}
          alt="Header Avatar"
        />
        <span>{user.fullname}</span>
        <FontAwesomeIcon icon={faChevronDown} className={classes.expandIcon} />
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        style={{ zIndex: 1500 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'right top' : 'center bottom',
            }}
          >
            <Paper className={classes.profileMenu}>
              <ClickAwayListener onClickAway={forceClose}>
                <MenuList
                  autoFocusItem={open}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                  className={classes.noPaddingMenu}
                >
                  {isEmulated && (
                    <MenuItem onClick={handleLogout}>
                      <FontAwesomeIcon
                        icon={faArrowRotateRight}
                        className={classes.decoIcon}
                        size="lg"
                      />
                      <Box ml={1.1}>
                        <Typography>
                          {t('profile_menu.link.revertImpersonate')}
                        </Typography>
                      </Box>
                    </MenuItem>
                  )}

                  {canUserAccess('navigation.links.delegations') && (
                    <Link
                      to={Constants.Routes.DELEGATIONS_PATH}
                      component={RouterLink}
                      key="delegations"
                      onClick={handleClose}
                    >
                      <MenuItem>
                        <FontAwesomeIcon
                          icon={faUsersGear}
                          className={classes.userIcon}
                          size="lg"
                        />
                        <Box ml={1}>
                          <Typography>
                            {t('profile_menu.link.delegations')}
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Link>
                  )}
                  {canUserAccess('navigation.links.admin_users') && (
                    <Link
                      to={Constants.Routes.ADMIN_USERS_PATH}
                      component={RouterLink}
                      key="admin-users"
                      onClick={handleClose}
                    >
                      <MenuItem>
                        <FontAwesomeIcon
                          icon={faShieldHalved}
                          className={classes.userIcon}
                          size="lg"
                        />
                        <Box ml={1}>
                          <Typography>
                            {t('profile_menu.link.admin_users')}
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Link>
                  )}
                  {canUserAccess('navigation.links.missing_agencies') && (
                    <Link
                      to={Constants.Routes.MISSING_AGENCIES_PATH}
                      component={RouterLink}
                      key="missing-agencies"
                      onClick={handleClose}
                    >
                      <MenuItem>
                        <FontAwesomeIcon
                          icon={faTriangleExclamation}
                          className={classes.userIcon}
                          size="lg"
                        />
                        <Box ml={1}>
                          <Typography>
                            {t('profile_menu.link.missing_agencies')}
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Link>
                  )}
                  <MenuItem onClick={handleLogout}>
                    <FontAwesomeIcon
                      icon={faPowerOff}
                      className={classes.decoIcon}
                      size="lg"
                    />
                    <Box ml={1.1}>
                      <Typography>{t('profile_menu.link.logout')}</Typography>
                    </Box>
                  </MenuItem>

                  <Divider className={classes.divider} />

                  <MenuItem className={classes.profileDetails}>
                    <Box mx={1}>
                      <>
                        <Typography>{mainUserRole}</Typography>
                        <Typography>{user.analyticalLabel}</Typography>

                        {isUserAssistantCG && (
                          <Box mt={1.5}>
                            <UserGroups />
                          </Box>
                        )}
                      </>
                    </Box>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default withTranslation()(ProfileMenu);
